import clsx from "clsx";
import { AnimatePresence, motion, wrap } from "framer-motion";
import React, { DetailedHTMLProps, FC, HTMLAttributes, useCallback, useEffect } from "react";
import { useState } from "react";

import { AnimationVariantsProps } from "../animation-variants-props";
import { SLIDE_TIMER } from "../slide-timer";
import { JunctionSection } from "./junction-section";

/**
 * Height of internal slides are bigger than the container
 * so it must be centered, also this has to be taken into
 * account when positioning them during exit and entering animations.
 *
 * Magic numbers have been obtained with the following formula: 🤓
 *  - center offset = ([height of slide] - [height of container]) / 2
 *  - top offset = [height of slide] + [center offset]
 *  - bottom offset = [height of slide] - [center offset]
 *
 * Also 1px was cut to prevent seeing the little transparent border during the animation.
 * TODO: We can reach the same result using only easing.
 */

// Note: I have chosen to keep the possibility to slide both up and down even if it is currently unused
// in case we'll choose to give some sort of control to the user about which slide to view
const variants = {
  enter: ({ mobile, direction }: AnimationVariantsProps) => {
    return mobile
      ? {
          y: direction > 0 ? "100vh" : "-100vh",
          zIndex: 0,
        }
      : {
          y: direction > 0 ? "639px" : "-895px",
          zIndex: 0,
        };
  },
  center: ({ mobile }: AnimationVariantsProps) => ({
    y: mobile ? 0 : "-128px",
    zIndex: 1,
  }),
  exit: ({ mobile, direction }: AnimationVariantsProps) => {
    return mobile
      ? {
          y: direction < 0 ? "100vh" : "-100vh",
          zIndex: 0,
        }
      : {
          y: direction < 0 ? "639px" : "-895px",
          zIndex: 0,
        };
  },
};

export type ZipTextBoxLabels = {
  title?: string | null;
  caption?: string | null;
};

export interface ZipTextBoxSliderProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  labels: ZipTextBoxLabels[];
  mobile?: boolean;
}

export const ZipTextBoxSlider: FC<ZipTextBoxSliderProps> = ({
  labels,
  mobile = false,
  className,
}) => {
  const [[page, direction], setPage] = useState([0, 0]);

  // We only have 3 images, but we paginate them absolutely (ie 1, 2, 3, 4, 5...) and
  // then wrap that within 0-2 to find our image ID in the array below. By passing an
  // absolute page index as the `motion` component's `key` prop, `AnimatePresence` will
  // detect it as an entirely new image. So you can infinitely paginate as few as 1 images.
  const labelIndex = wrap(0, labels.length, page);

  const paginate = useCallback(
    (newDirection: number) => {
      setPage([page + newDirection, newDirection]);
    },
    [page]
  );

  useEffect(() => {
    if (labels?.length > 1) {
      const interval = setInterval(() => paginate(1), SLIDE_TIMER);
      return () => clearInterval(interval);
    }
  }, [paginate, labels?.length]);

  return (
    <div className={clsx(["h-screen lg:h-128 overflow-hidden", className])}>
      <AnimatePresence initial={false} custom={{ direction, mobile }} mode="popLayout">
        <motion.div
          className={clsx([
            "h-full lg:h-192 w-full",
            "flex flex-col lg:flex-row lg:justify-between items-start lg:items-center",
            "pl-8 lg:pl-0",
          ])}
          key={page}
          custom={{ mobile, direction }}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            y: { ease: "easeOut", duration: 0.9 },
          }}
        >
          <div
            className={clsx([
              "flex-[0_0_50%]",
              "flex items-end lg:items-center",
              "pt-7 lg:pt-0 pr-8 lg:pl-14 lg:pr-24",
              "text-4xl lg:text-5xl text-light",
            ])}
          >
            {labels[labelIndex].title}
          </div>
          <div className="flex flex-col h-full w-full flex-[0_0_50%]">
            <JunctionSection variant="top" />
            <div
              className={clsx([
                "min-h-[200px] lg:flex-[0_0_50%]",
                "my-[-1px] bg-neutral rounded-l-3xl",
                "flex items-center",
                "py-9 pl-5 lg:pl-14 pr-10 lg:pr-24",
                "text-2xl lg:text-4xl leading-snug lg:leading-tight text-primary font-normal",
              ])}
            >
              {labels[labelIndex].caption}
            </div>
            <JunctionSection variant="bottom" />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
